import React, { useState, useEffect } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Typography, Avatar, Box, Checkbox, Dialog,
  Chip, Card, CardContent, Collapse, Stack, Tooltip, Switch, List, ListItem, ListItemText, ListItemIcon, Popover, Button,
  Divider, Grid, useTheme, useMediaQuery
} from '@mui/material';
import { 
  SchoolRounded, CurrencyRupee, CloudUpload as CloudUploadIcon, PictureAsPdf as PdfIcon,
  ViewColumn as ViewColumnIcon, Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon,
  Person as PersonIcon, Phone as PhoneIcon, Email as EmailIcon, CalendarToday as CalendarTodayIcon,
  School as SchoolIcon, Work as WorkIcon, Badge as BadgeIcon, ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon, Edit as EditIcon, Delete as DeleteIcon, AccountBalance as AccountBalanceIcon,
  Public as PublicIcon, LocationCity as LocationCityIcon, Home as HomeIcon
} from '@mui/icons-material';
import AttendanceView from '../dashboard/AttendanceView';
import { Clock, LogIn } from "lucide-react";
import ModalView from '../model/ModelView';
import EmployeeRegister from '../register/EmployeeRegister';
import { useNavigate } from 'react-router-dom';
import SalarySlipPage from '../pages/SalarySlipPage';
import authService from '../Auth/AuthService';
import FileUploadPage from '../model/FileUploadPage';

const TeacherList = ({ teachers, setIds }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const [openForm, setOpenForm] = useState(false);
  const [openEmp, setOpenEmp] = useState(false);
  const [openPdf, setOpenPdf] = useState(false);
  const [salaryForm, setSalaryForm] = useState(false);
  const [selected, setSelected] = useState({});
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState([]);
  const tokenInfo = authService.tokenInfo();
  const DOC_URL = authService.DOC_URL
  const [showPdf, setShowPdf] = useState(null);
  const [open, setOpen] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});

  // Column customization state - All fields from JSON
  const [columnVisibility, setColumnVisibility] = useState({
    select: true,
    image: true,
    employeeCode: true,
    fullName: true,
    email: !isMobile,
    phone: true,
    gender: !isMobile,
    dateOfBirth: !isMobile,
    address: false,
    city: false,
    state: false,
    qualification: !isMobile,
    specialization: false,
    degree: false,
    company: false,
    motherTongue: false,
    joiningDate: !isMobile,
    salary: !isMobile,
    maritalStatus: false,
    accountDetails: false,
    experience: !isMobile,
    isActive: true,
    actions: true,
  });

  const [columnOrder, setColumnOrder] = useState([
    'select',
    'image',
    'employeeCode',
    'fullName',
    'email',
    'phone',
    'gender',
    'dateOfBirth',
    'address',
    'city',
    'state',
    'qualification',
    'specialization',
    'degree',
    'company',
    'motherTongue',
    'joiningDate',
    'salary',
    'maritalStatus',
    'accountDetails',
    'experience',
    'isActive',
    'actions'
  ]);

  const [anchorEl, setAnchorEl] = useState(null);
  const openColumnPopover = Boolean(anchorEl);

  const columnConfig = {
    select: { label: "Select", icon: <Checkbox /> },
    image: { label: "Photo", icon: <Avatar /> },
    employeeCode: { label: "Employee Code", icon: <BadgeIcon /> },
    fullName: { label: "Full Name", icon: <PersonIcon /> },
    email: { label: "Email", icon: <EmailIcon /> },
    phone: { label: "Phone", icon: <PhoneIcon /> },
    gender: { label: "Gender", icon: <PersonIcon /> },
    dateOfBirth: { label: "DOB", icon: <CalendarTodayIcon /> },
    address: { label: "Address", icon: <HomeIcon /> },
    city: { label: "City", icon: <LocationCityIcon /> },
    state: { label: "State", icon: <LocationCityIcon /> },
    qualification: { label: "Qualification", icon: <SchoolIcon /> },
    specialization: { label: "Specialization", icon: <SchoolIcon /> },
    degree: { label: "Degree", icon: <SchoolIcon /> },
    company: { label: "Company", icon: <WorkIcon /> },
    motherTongue: { label: "Mother Tongue", icon: <PublicIcon /> },
    joiningDate: { label: "Joining Date", icon: <CalendarTodayIcon /> },
    salary: { label: "Salary", icon: <CurrencyRupee /> },
    maritalStatus: { label: "Marital Status", icon: <PersonIcon /> },
    accountDetails: { label: "Account Details", icon: <AccountBalanceIcon /> },
    experience: { label: "Experience", icon: <WorkIcon /> },
    isActive: { label: "Status", icon: <BadgeIcon /> },
    actions: { label: "Actions", icon: <EditIcon /> },
  };

  // Load column preferences
  const loadColumnPreferences = () => {
    try {
      const savedVisibility = localStorage.getItem('teacherlist_columnVisibility');
      const savedOrder = localStorage.getItem('teacherlist_columnOrder');
      
      if (savedVisibility) {
        const parsed = JSON.parse(savedVisibility);
        setColumnVisibility(prev => ({ ...prev, ...parsed }));
      }
      if (savedOrder) {
        setColumnOrder(JSON.parse(savedOrder));
      }
    } catch (error) {
      console.error('Error loading column preferences:', error);
    }
  };

  const saveColumnPreferences = (visibility, order) => {
    try {
      localStorage.setItem('teacherlist_columnVisibility', JSON.stringify(visibility));
      localStorage.setItem('teacherlist_columnOrder', JSON.stringify(order));
    } catch (error) {
      console.error('Error saving column preferences:', error);
    }
  };

  const handleColumnToggle = (columnKey) => {
    if (columnKey === 'select') return;
    
    setColumnVisibility(prev => {
      const newVisibility = {
        ...prev,
        [columnKey]: !prev[columnKey]
      };
      saveColumnPreferences(newVisibility, columnOrder);
      return newVisibility;
    });
  };

  const handleResetColumns = () => {
    const defaultVisibility = {
      select: true,
      image: true,
      employeeCode: true,
      fullName: true,
      email: false,
      phone: true,
      gender: !isMobile,
      dateOfBirth: !isMobile,
      address: false,
      city: false,
      state: false,
      qualification: !isMobile,
      specialization: false,
      degree: false,
      company: false,
      motherTongue: false,
      joiningDate: false,
      salary: false,
      maritalStatus: false,
      accountDetails: false,
      experience: !isMobile,
      isActive: true,
      actions: true,
    };
    const defaultOrder = [
      'select',
      'image',
      'employeeCode',
      'fullName',
      'email',
      'phone',
      'gender',
      'dateOfBirth',
      'address',
      'city',
      'state',
      'qualification',
      'specialization',
      'degree',
      'company',
      'motherTongue',
      'joiningDate',
      'salary',
      'maritalStatus',
      'accountDetails',
      'experience',
      'isActive',
      'actions'
    ];
    setColumnVisibility(defaultVisibility);
    setColumnOrder(defaultOrder);
    saveColumnPreferences(defaultVisibility, defaultOrder);
  };

  const handleColumnPopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleColumnPopoverClose = () => {
    setAnchorEl(null);
  };

  const getVisibleColumns = () => {
    return columnOrder.filter(col => columnVisibility[col] !== false);
  };

  useEffect(() => {
    loadColumnPreferences();
  }, []);

  const isAllSelected =
    teachers.length > 0 && selectedIds.length === teachers.length;

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(teachers.map(t => t.id));
      setIds(teachers.map(t => t.id));
    } else {
      setSelectedIds([]);
      setIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds(prev =>
      prev.includes(id)
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
    setIds(prev =>
      prev.includes(id)
        ? prev.filter(item => item !== id)
        : [...prev, id]);
  };

  const handleClose = () => {
    setOpenForm(false);
    setOpenEmp(false);
    setSalaryForm(false);
    setOpenPdf(false);
  };

  const createCertificate = (data) => {
    const employee = {
      code: data.employeeCode,
      name: [data.firstName, data.lastName].join(" "),
      designation: "Senior " + data.specialization + " Teacher",
      department: data.department != null ? data.department : "Academics",
      empId: data.id,
      gender: data.gender,
      joiningDate: data.joiningDate,
      active: data.isActive,
    };
    navigate('/certificate-emp', {
      state: {
        emp: employee
      },
    });
  };

  function IframePDFViewer({ fileUrl }) {
    return (
      <iframe
        src={DOC_URL + fileUrl}
        width="100%"
        height="600px"
        title="PDF Viewer"
      />
    );
  }

  const handleSubmit = async (employeeData) => {
    try {
      const url = authService.pathUrl('PUT', `/employees/${employeeData.id}`);
      await authService.putAuth(url, employeeData);
      handleClose();
    } catch (error) {
      console.error("Error saving employee:", error);
    }
  }

  function toTitleCase(str) {
    if (!str) return 'N/A';
    return str
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }

  function formatDate(dateString) {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  }

  function formatCurrency(amount) {
    if (amount === null || amount === undefined) return '₹0.00';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(amount);
  }

  const toggleCard = (id) => {
    setExpandedCards((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  if (!teachers || teachers.length === 0) {
    return <Typography sx={{ p: 2 }}>No teacher data found</Typography>;
  }

  // Render mobile card view
  const renderMobileCard = (teacher) => {
    const isExpanded = expandedCards[teacher.id] || false;
    
    return (
      <Card 
        key={teacher.id} 
        sx={{ 
          mb: 2, 
          borderLeft: `4px solid ${teacher.isActive !== false ? '#4caf50' : '#e0e0e0'}`,
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Box display="flex" alignItems="center" gap={2}>
            {/* <Checkbox
              checked={selectedIds.includes(teacher.id)}
              onChange={() => handleSelectOne(teacher.id)}
              size="small"
            /> */}
            <Avatar 
              src={teacher.photo ? DOC_URL + teacher.photo : null} 
              sx={{ width: 50, height: 50 }}
            >
              {teacher.firstName?.charAt(0) || 'T'}
            </Avatar>
            <Box flex={1}>
              <Typography variant="subtitle1" fontWeight="bold">
                {toTitleCase(`${teacher.firstName || ''} ${teacher.lastName || ''}`)}
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Chip 
                  label={teacher.isActive !== false ? 'Active' : 'Inactive'} 
                  color={teacher.isActive !== false ? 'success' : 'default'} 
                  size="small"
                />
                {teacher.employeeCode && (
                  <Chip 
                    label={teacher.employeeCode} 
                    size="small"
                    variant="outlined"
                  />
                )}
                {teacher.gender && (
                  <Chip 
                    label={teacher.gender} 
                    size="small"
                    color={teacher.gender === 'MALE' ? 'primary' : 'secondary'}
                  />
                )}
              </Box>
            </Box>
            <IconButton onClick={() => toggleCard(teacher.id)} size="small">
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          
          <Box display="flex" flexWrap="wrap" gap={2} mt={1.5}>
            {teacher.phone && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <PhoneIcon fontSize="small" color="action" />
                <Typography variant="body2">{teacher.phone}</Typography>
              </Box>
            )}
            {teacher.email && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <EmailIcon fontSize="small" color="action" />
                <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{teacher.email}</Typography>
              </Box>
            )}
            {teacher.salary && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <CurrencyRupee fontSize="small" color="action" />
                <Typography variant="body2">{formatCurrency(teacher.salary)}</Typography>
              </Box>
            )}
            {teacher.joiningDate && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <CalendarTodayIcon fontSize="small" color="action" />
                <Typography variant="body2">{formatDate(teacher.joiningDate)}</Typography>
              </Box>
            )}
          </Box>
        </CardContent>
        
        <Collapse in={isExpanded}>
          <Divider />
          <CardContent sx={{ pt: 1, pb: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Qualification</Typography>
                <Typography variant="body2">{teacher.qualification || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Experience</Typography>
                <Typography variant="body2">{teacher.experience || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">DOB</Typography>
                <Typography variant="body2">{formatDate(teacher.dateOfBirth)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Specialization</Typography>
                <Typography variant="body2">{teacher.specialization || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Marital Status</Typography>
                <Typography variant="body2">{teacher.maritalStatus || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Mother Tongue</Typography>
                <Typography variant="body2">{teacher.tongue || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">Address</Typography>
                <Typography variant="body2">
                  {teacher.addressDTO ? 
                    `${teacher.addressDTO.addressLine1 || ''}, ${teacher.addressDTO.city || ''}, ${teacher.addressDTO.state || ''} - ${teacher.addressDTO.pincode || ''}` 
                    : 'N/A'}
                </Typography>
              </Grid>
              {teacher.accountDto && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">Account Details</Typography>
                  <Typography variant="body2">
                    {teacher.accountDto.bankname || ''} - {teacher.accountDto.acctnumber || ''} ({teacher.accountDto.branch || ''})
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
          
          <Divider />
          <Box sx={{ p: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            <Tooltip title="Experience Certificate">
              <IconButton size="small" onClick={() => createCertificate(teacher)} color="warning">
                <SchoolRounded fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="View Salary">
              <IconButton size="small" onClick={() => { setSelected(teacher.id); setSalaryForm(true); }} color="warning">
                <CurrencyRupee fontSize="small" />
              </IconButton>
            </Tooltip>
            {tokenInfo?.is_permited.Attendance && (
              <Tooltip title="View Attendance">
                <IconButton size="small" onClick={() => { setSelected(teacher.id); setOpenForm(true); }} color="warning">
                  <Clock size={20} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Create Login">
              <IconButton size="small" onClick={() => { setOpenEmp(true); setSelected(teacher); }} color="warning">
                <LogIn size={20} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Upload Documents">
              <IconButton size="small" onClick={() => { setOpenPdf(true); setSelected(teacher); }} color="warning">
                <CloudUploadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {teacher?.documents && (
              <Tooltip title="View Documents">
                <IconButton size="small" onClick={() => { setShowPdf(teacher?.documents); setOpen(!open); }} color="warning">
                  <PdfIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Collapse>
      </Card>
    );
  };

  // Render column header
  const renderColumnHeader = (columnKey) => {
    switch(columnKey) {
      case 'select':
        return (
          <TableCell padding="checkbox">
            <Checkbox
              checked={isAllSelected}
              indeterminate={selectedIds.length > 0 && selectedIds.length < teachers.length}
              onChange={handleSelectAll}
              size={isMobile ? "small" : "medium"}
            />
          </TableCell>
        );
      case 'image':
        return <TableCell><strong>Photo</strong></TableCell>;
      case 'employeeCode':
        return <TableCell><strong>Emp Code</strong></TableCell>;
      case 'fullName':
        return <TableCell><strong>Full Name</strong></TableCell>;
      case 'email':
        return <TableCell><strong>Email</strong></TableCell>;
      case 'phone':
        return <TableCell><strong>Phone</strong></TableCell>;
      case 'gender':
        return <TableCell><strong>Gender</strong></TableCell>;
      case 'dateOfBirth':
        return <TableCell><strong>DOB</strong></TableCell>;
      case 'address':
        return <TableCell><strong>Address</strong></TableCell>;
      case 'city':
        return <TableCell><strong>City</strong></TableCell>;
      case 'state':
        return <TableCell><strong>State</strong></TableCell>;
      case 'qualification':
        return <TableCell><strong>Qualification</strong></TableCell>;
      case 'specialization':
        return <TableCell><strong>Specialization</strong></TableCell>;
      case 'degree':
        return <TableCell><strong>Degree</strong></TableCell>;
      case 'company':
        return <TableCell><strong>Company</strong></TableCell>;
      case 'motherTongue':
        return <TableCell><strong>Mother Tongue</strong></TableCell>;
      case 'joiningDate':
        return <TableCell><strong>Joining Date</strong></TableCell>;
      case 'salary':
        return <TableCell><strong>Salary</strong></TableCell>;
      case 'maritalStatus':
        return <TableCell><strong>Marital Status</strong></TableCell>;
      case 'accountDetails':
        return <TableCell><strong>Account Details</strong></TableCell>;
      case 'experience':
        return <TableCell><strong>Experience</strong></TableCell>;
      case 'isActive':
        return <TableCell><strong>Status</strong></TableCell>;
      case 'actions':
        return <TableCell align="right"><strong>Actions</strong></TableCell>;
      default:
        return null;
    }
  };

  // Render column cell
  const renderColumnCell = (columnKey, teacher) => {
    switch(columnKey) {
      case 'select':
        return (
          <TableCell padding="checkbox">
            <Checkbox
              checked={selectedIds.includes(teacher.id)}
              onChange={() => handleSelectOne(teacher.id)}
              size={isMobile ? "small" : "medium"}
            />
          </TableCell>
        );
      case 'image':
        return (
          <TableCell>
            <Avatar 
              src={teacher.photo ? DOC_URL + teacher.photo : null} 
              sx={{ width: 40, height: 40 }}
            >
              {teacher.firstName?.charAt(0) || 'T'}
            </Avatar>
          </TableCell>
        );
      case 'employeeCode':
        return <TableCell>{teacher.employeeCode || 'N/A'}</TableCell>;
      case 'fullName':
        return <TableCell>{toTitleCase(`${teacher.firstName || ''} ${teacher.lastName || ''}`)}</TableCell>;
      case 'email':
        return <TableCell>{teacher.email || 'N/A'}</TableCell>;
      case 'phone':
        return <TableCell>{teacher.phone || 'N/A'}</TableCell>;
      case 'gender':
        return (
          <TableCell>
            <Chip
              label={teacher.gender || 'N/A'}
              size="small"
              color={teacher.gender === 'MALE' ? 'primary' : teacher.gender === 'FEMALE' ? 'secondary' : 'default'}
            />
          </TableCell>
        );
      case 'dateOfBirth':
        return <TableCell>{formatDate(teacher.dateOfBirth)}</TableCell>;
      case 'address':
        return (
          <TableCell sx={{ maxWidth: 150 }}>
            {teacher.addressDTO ? 
              `${teacher.addressDTO.addressLine1 || ''}, ${teacher.addressDTO.city || ''}` 
              : 'N/A'}
          </TableCell>
        );
      case 'city':
        return <TableCell>{teacher.addressDTO?.city || 'N/A'}</TableCell>;
      case 'state':
        return <TableCell>{teacher.addressDTO?.state || 'N/A'}</TableCell>;
      case 'qualification':
        return <TableCell>{teacher.qualification || 'N/A'}</TableCell>;
      case 'specialization':
        return <TableCell>{teacher.specialization || 'N/A'}</TableCell>;
      case 'degree':
        return <TableCell>{teacher.degree || 'N/A'}</TableCell>;
      case 'company':
        return <TableCell>{teacher.company || 'N/A'}</TableCell>;
      case 'motherTongue':
        return <TableCell>{teacher.tongue || 'N/A'}</TableCell>;
      case 'joiningDate':
        return <TableCell>{formatDate(teacher.joiningDate)}</TableCell>;
      case 'salary':
        return <TableCell>{formatCurrency(teacher.salary)}</TableCell>;
      case 'maritalStatus':
        return <TableCell>{teacher.maritalStatus || 'N/A'}</TableCell>;
      case 'accountDetails':
        return (
          <TableCell>
            {teacher.accountDto ? (
              <Tooltip title={`${teacher.accountDto.bankname || ''} - ${teacher.accountDto.acctnumber || ''} (${teacher.accountDto.branch || ''})`}>
                <Chip
                  label={teacher.accountDto.bankname || 'N/A'}
                  size="small"
                  variant="outlined"
                />
              </Tooltip>
            ) : 'N/A'}
          </TableCell>
        );
      case 'experience':
        return <TableCell>{teacher.experience || 'N/A'}</TableCell>;
      case 'isActive':
        return (
          <TableCell>
            <Chip
              label={teacher.isActive !== false ? "Active" : "Inactive"}
              color={teacher.isActive !== false ? "success" : "default"}
              size="small"
            />
          </TableCell>
        );
      case 'actions':
        return (
          <TableCell align="right">
            <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap">
              <Tooltip title="Experience Certificate">
                <IconButton size="small" onClick={() => createCertificate(teacher)} color="warning">
                  <SchoolRounded fontSize={isMobile ? "small" : "medium"} />
                </IconButton>
              </Tooltip>
              <Tooltip title="View Salary">
                <IconButton size="small" onClick={() => { setSelected(teacher.id); setSalaryForm(true); }} color="warning">
                  <CurrencyRupee fontSize={isMobile ? "small" : "medium"} />
                </IconButton>
              </Tooltip>
              {tokenInfo?.is_permited.Attendance && (
                <Tooltip title="View Attendance">
                  <IconButton size="small" onClick={() => { setSelected(teacher.id); setOpenForm(true); }} color="warning">
                    <Clock size={isMobile ? 18 : 20} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Create Login">
                <IconButton size="small" onClick={() => { setOpenEmp(true); setSelected(teacher); }} color="warning">
                  <LogIn size={isMobile ? 18 : 20} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Upload Documents">
                <IconButton size="small" onClick={() => { setOpenPdf(true); setSelected(teacher); }} color="warning">
                  <CloudUploadIcon fontSize={isMobile ? "small" : "medium"} />
                </IconButton>
              </Tooltip>
              {teacher?.documents && (
                <Tooltip title="View Documents">
                  <IconButton size="small" onClick={() => { setShowPdf(teacher?.documents); setOpen(!open); }} color="warning">
                    <PdfIcon fontSize={isMobile ? "small" : "medium"} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </TableCell>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ mb: 2, width: "100%" }}>
      {/* Column Customization Button */}
      <Box display="flex" justifyContent="flex-end" mb={1}>
        <Tooltip title="Customize Columns">
          <IconButton onClick={handleColumnPopoverOpen} color="warning" size={isMobile ? "small" : "medium"}>
            <ViewColumnIcon sx={{ border: "2px solid", fontWeight: "bold", borderRadius: "10px" }} fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Column Customization Popover */}
      <Popover
        open={openColumnPopover}
        anchorEl={anchorEl}
        onClose={handleColumnPopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: isMobile ? 280 : 350,
            maxHeight: 500,
            p: 2,
          }
        }}
      >
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontSize="1rem" fontWeight="bold">
              Customize Columns
            </Typography>
            <Button size="small" onClick={handleResetColumns}>
              Reset
            </Button>
          </Box>
          
          <Divider />
          
          <List sx={{ pt: 1, overflow: 'auto', maxHeight: 350 }}>
            {columnOrder.map((colKey) => {
              const config = columnConfig[colKey];
              if (!config) return null;
              const isVisible = columnVisibility[colKey] !== false;
              const isSelectColumn = colKey === 'select';
              
              return (
                <ListItem
                  key={colKey}
                  dense
                  sx={{
                    borderRadius: 1,
                    '&:hover': { backgroundColor: 'action.hover' },
                    opacity: isSelectColumn ? 0.7 : 1,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {config.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={config.label}
                    secondary={isSelectColumn ? "Required" : ""}
                    primaryTypographyProps={{ variant: isMobile ? 'caption' : 'body2' }}
                  />
                  <Switch
                    edge="end"
                    checked={isVisible}
                    onChange={() => handleColumnToggle(colKey)}
                    disabled={isSelectColumn}
                    color="primary"
                    size="small"
                  />
                  {isVisible ? (
                    <VisibilityIcon fontSize="small" color="primary" sx={{ ml: 1 }} />
                  ) : (
                    <VisibilityOffIcon fontSize="small" color="disabled" sx={{ ml: 1 }} />
                  )}
                </ListItem>
              );
            })}
          </List>
          
          <Divider sx={{ my: 1 }} />
          
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'center' }}>
            {getVisibleColumns().length} of {Object.keys(columnConfig).length} columns visible
          </Typography>
        </Box>
      </Popover>

      {isMobile ? (
        // Mobile Card View
        <Box>
          {teachers.map((teacher) => renderMobileCard(teacher))}
        </Box>
      ) : (
        // Desktop Table View
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size={isTablet ? "small" : "medium"} stickyHeader>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                {getVisibleColumns().map((colKey) => renderColumnHeader(colKey))}
              </TableRow>
            </TableHead>
            <TableBody>
              {teachers.map((teacher) => (
                <TableRow key={teacher.id} hover>
                  {getVisibleColumns().map((colKey) => renderColumnCell(colKey, teacher))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Modals */}
      <ModalView
        size="lg"
        open={openForm}
        handleClose={handleClose}
        title="View Attendance"
        content={
          <AttendanceView
            empId={selected}
            onSuccess={() => setOpenForm(false)}
          />
        }
      />

      <ModalView
        size="md"
        open={salaryForm}
        handleClose={handleClose}
        title="View Salary"
        content={
          <SalarySlipPage
            empId={selected}
            onSuccess={() => setSalaryForm(false)}
          />
        }
      />

      <ModalView
        size="md"
        open={openEmp}
        handleClose={handleClose}
        title="Create Login"
        content={
          <EmployeeRegister
            email={selected.email}
            mobile={selected.phone}
            onSuccess={() => setOpenEmp(false)}
          />
        }
      />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
        <IframePDFViewer fileUrl={showPdf} />
      </Dialog>

      <ModalView
        size="sm"
        open={openPdf}
        handleClose={handleClose}
        title="Upload Document/Photo"
        content={
          <FileUploadPage
            Id={selected?.id}
            Type="EMP"
            onSuccess={(fileData, type) => {
              const updatedForm = {
                ...selected,
                photo: type === "photo"
                  ? fileData?.path
                  : selected?.photo,
                documents: type === "pdf"
                  ? fileData?.path
                  : selected?.documents
              };
              handleSubmit(updatedForm);
              setOpenPdf(false);
            }}
          />
        }
      />
    </Box>
  );
};

export default TeacherList;